import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  sendEmailVerification,
  sendPasswordResetEmail,
  reload,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db, firebaseReady, getFirebaseDiagnostics } from '../firebase/firebase';
import { translateFirebaseError } from '../services/callableService';
import jurorOutbox from '../features/juror/services/jurorOutbox';
import {
  ACCESS_STATUS_APPROVED,
  ACCESS_STATUS_PENDING,
  getInitialAccessStatus,
  isApprovedAccessStatus,
  isBootstrapApprovedEmail,
} from '../features/auth/utils/access';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  async function signup(email, password) {
    try {
      await firebaseReady;
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      setCurrentUser(credential.user);
      return credential;
    } catch (error) {
      throw translateFirebaseError(error, 'auth');
    }
  }

  async function login(email, password) {
    try {
      await firebaseReady;
      const credential = await signInWithEmailAndPassword(auth, email, password);
      setCurrentUser(credential.user);
      return credential;
    } catch (error) {
      throw translateFirebaseError(error, 'auth');
    }
  }

  async function logout() {
    const departingUserId = auth.currentUser?.uid || currentUser?.uid || null;
    await signOut(auth);
    setCurrentUser(null);
    if (departingUserId) {
      await jurorOutbox.clearForActor(departingUserId);
    }
  }

  async function resetPassword(email) {
    try {
      await firebaseReady;
      return await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw translateFirebaseError(error, 'auth');
    }
  }

  function sendVerificationEmail() {
    if (!auth.currentUser) {
      return Promise.reject(new Error('Sign in before requesting verification.'));
    }
    return firebaseReady
      .then(() => sendEmailVerification(auth.currentUser))
      .catch((error) => Promise.reject(translateFirebaseError(error, 'auth')));
  }

  async function refreshUser() {
    if (!auth.currentUser) return null;
    await reload(auth.currentUser);
    setCurrentUser({ ...auth.currentUser });
    return auth.currentUser;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) {
      setUserProfile(null);
      setProfileLoading(false);
      return undefined;
    }

    setProfileLoading(true);

    const unsubscribe = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : null;
        const normalizedEmail = data?.email || currentUser.email || '';
        const normalizedAccessStatus = isBootstrapApprovedEmail(normalizedEmail)
          ? ACCESS_STATUS_APPROVED
          : data?.accessStatus || getInitialAccessStatus(currentUser.email);
        setUserProfile(
          data
            ? {
                ...data,
                email: normalizedEmail,
                accessStatus: normalizedAccessStatus,
              }
            : {
                email: normalizedEmail,
                accessStatus: normalizedAccessStatus,
              }
        );
        setProfileLoading(false);
      },
      (error) => {
        console.error('Failed to load user profile:', error);
        setUserProfile({
          email: currentUser.email || '',
          accessStatus: isBootstrapApprovedEmail(currentUser.email)
            ? getInitialAccessStatus(currentUser.email)
            : ACCESS_STATUS_PENDING,
        });
        setProfileLoading(false);
      }
    );

    return unsubscribe;
  }, [currentUser]);

  const accessStatus = userProfile?.accessStatus || getInitialAccessStatus(currentUser?.email);
  const isBootstrapUser = isBootstrapApprovedEmail(currentUser?.email);
  const isApprovedUser = Boolean(currentUser) && (isBootstrapUser || isApprovedAccessStatus(accessStatus));

  const value = {
    currentUser,
    userProfile,
    accessStatus,
    isBootstrapUser,
    isApprovedUser,
    profileLoading,
    signup,
    login,
    logout,
    resetPassword,
    sendVerificationEmail,
    refreshUser,
    isEmailVerified: Boolean(currentUser?.emailVerified),
    firebaseDiagnostics: getFirebaseDiagnostics(),
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && !profileLoading && children}
    </AuthContext.Provider>
  );
}
